// The single place the SPA talks to the server.
//
// Every request goes through one function so the access token, the error shape, and the
// sign-out-on-401 behavior have exactly one implementation. A component that reached for `fetch`
// directly would be the one that forgot the token, or the one that swallowed a 403.

import type { ConsolePreference } from './preferences'
import type { PublicBranding, TenantBranding } from './branding'
import { shouldRenew } from './sessionClock'
import { routePath } from './orgPath'

export interface Page<TItem> {
  items: TItem[]
  total: number
  offset: number
  limit: number
  hasMore: boolean
}

export interface SignInResponse {
  token: string
  expiresUtc: string
  tenantId: string
  accountId: string
  displayName: string
  roles: string[]
  tenantSlug: string
  tenantName: string | null
  mustChangePassword: boolean
  /**
   * The other reason the console renders the change screen instead of itself: this organization has
   * decided this password is too old. Carried on a successful sign-in, because expiry is not an
   * authentication failure - the password is right, and refusing would leave the account no way to
   * reach the endpoint that fixes it.
   */
  passwordExpired?: boolean
  /**
   * Set only while this session is a platform administrator looking at somebody else's
   * organization. Its absence is what an ordinary sign-in looks like, and the masthead draws its
   * banner from exactly this.
   */
  assumedByName?: string | null
}

/**
 * A renewed session, as the server answers it.
 *
 * Deliberately `SignInResponse` without `tenantName`: the name is not on the token and the console
 * already has it, so the server does not fetch it again to tell somebody something they are looking
 * at. Merged over the identity already held, which is also what keeps `assumedByName` alive.
 */
export interface RefreshResponse {
  token: string
  expiresUtc: string
  /** When this session ends for good, renewals included, so the console can say so rather than be surprised. */
  sessionExpiresUtc: string
  tenantId: string
  accountId: string
  email: string
  displayName: string
  roles: string[]
  tenantSlug: string
  mustChangePassword: boolean
}

/**
 * What happened to the session, for anything that renders it.
 *
 * This exists because `request` used to clear the token on a 401 and tell nobody. `App` seeds its
 * identity once from storage, so the console went on rendering as though somebody were signed in,
 * with no token - and every screen showed its own error until the page was reloaded by hand. That is
 * the shape the complaint took: not "it signed me out" so much as "it stopped working and I had to
 * reload to find out why".
 */
export type SessionEvent =
  | { kind: 'ended'; reason: 'session-expired' | 'no-session'; at: string }
  /** A support session ended and the platform session behind it is active again (D-299). */
  | { kind: 'released'; identity: SignInResponse }
  | { kind: 'renewed'; identity: SignInResponse }

export interface AccountSummary {
  accountId: string
  email: string
  displayName: string
  roles: string[]
  status: string
  mustChangePassword: boolean
  createdUtc: string
  lastSignInUtc: string | null
  lockedUntilUtc: string | null
  /** Null for an account that predates ageing, which therefore never expires. */
  passwordChangedUtc: string | null
}

/** How long an organization lets a password stand, and who a change would catch. */
export interface PasswordPolicy {
  maxAgeDays: number
  warnDays: number
  updatedUtc: string | null
  /**
   * How many accounts the saved maximum age holds at the change screen right now.
   *
   * The difference between a policy and a surprise: typing 9 where 90 was meant is a plausible slip,
   * and this is the only honest way to say what it would cost.
   */
  wouldExpireNow: number
}

/** What a deletion destroyed, reported back so that it is never a silent act. */
export interface TenantDeletionCounts {
  bodies: number
  events: number
  devices: number
  accounts: number
}

/**
 * The organization list, and whether this deployment allows deleting one.
 *
 * The flag rides on the list rather than sitting in its own endpoint because it is only ever needed
 * where the list is drawn, and a screen that offers a button the server has not mapped is worse than
 * one that offers none.
 */
export interface TenantPage extends Page<TenantSummary> {
  deletionEnabled: boolean
}

export interface TenantSummary {
  tenantId: string
  name: string
  slug: string
  status: string
  createdUtc: string
  accounts: number
  devices: number
  /**
   * An organization without a signing key is served no policy at all, and the first symptom is a
   * device whose feed is silently empty. Provisioning has always reported it; nothing showed it
   * afterwards until this screen.
   */
  hasSigningKey: boolean
}

export interface TenantAssumption {
  assumptionId: string
  platformEmail: string
  platformName: string
  roles: string[]
  reason: string
  startedUtc: string
  expiresUtc: string
}

export interface PlatformAdminSummary {
  accountId: string
  email: string
  displayName: string
  status: string
  mustChangePassword: boolean
  createdUtc: string
  lastSignInUtc: string | null
  lockedUntilUtc: string | null
  passwordChangedUtc: string | null
}

export interface AssumedSession {
  token: string
  expiresUtc: string
  tenantId: string
  tenantName: string
  tenantSlug: string
  roles: string[]
}

export interface PolicySummary {
  policyId: string
  name: string
  description: string | null
  capability: string
  status: string
  createdUtc: string
  latestRevision: number
  approvedRevision: number | null
  approvedHash: string | null
  approvedUtc: string | null
  /**
   * Where the newest revision came from, when it did not come from somebody typing.
   *
   * Absent for almost every policy - the server omits null members - which is what lets the Policies
   * screen say how many of the drafts waiting for review were produced from cases rather than written
   * by hand. Those are the ones somebody else asked for and is waiting on.
   */
  latestSourceKind?: string | null
  /**
   * The builder group this policy belongs to, when the policy builder made it (ADR-S-052). Absent for
   * every policy written by hand. `groupSize` is counted by the server over the whole group under the
   * same status filter, never from the rows on this page, so a group the page cut in half still says
   * how big it is.
   */
  groupId?: string | null
  groupName?: string | null
  groupRole?: 'recipe' | 'screenshot' | null
  groupAgentKey?: string | null
  groupSize?: number
}

export type ValidationSeverity = 'error' | 'warning'

export interface ValidationMessage {
  severity: ValidationSeverity
  fieldPath: string
  message: string
  suggestion?: string | null
}

export interface PolicyRevisionDetail {
  policyId: string
  revision: number
  name: string
  description: string | null
  capability: string
  status: string
  documentJson: string
  documentHash: string
  notes: string | null
  createdUtc: string
  approvedUtc: string | null
  validation: ValidationMessage[]
  availableRevisions: number[]
  /** The builder group this policy belongs to; absent for a policy written by hand. */
  groupId?: string | null
  groupName?: string | null
}

export interface DeviceSummary {
  deviceId: string
  deviceName: string
  hostname?: string | null
  operatingSystem: string
  osVersion?: string | null
  clientVersion: string
  principalUser?: string | null
  status: string
  envelopeSchemaVersion: number
  enrolledUtc: string
  // Absent rather than null when there has been none. See DeviceDetail.
  lastSeenUtc?: string | null
  lastCheckInUtc?: string | null
}

/** One policy this deployment cannot serve to a device, and why not. */
export interface WithheldPolicy {
  policyId: string
  revision: number
  servable: boolean
  reason: string | null
}

/**
 * A device, and what can and cannot be served to it.
 *
 * `lastRejectionUtc` is deliberately **not** on this type, though the endpoint sends it. The check-in
 * body carries no rejection time, so the server stamps the moment it heard — and the device re-reports
 * the same rejection at every check-in, so the value advances forever and always reads "just now". A
 * rejection from March would look like it happened seconds ago. There is no honest label for it, so it
 * is kept out of reach until the wire contract carries a real one.
 */
export interface DeviceDetail {
  deviceId: string
  deviceName: string
  hostname: string | null
  installId: string | null
  operatingSystem: string
  osVersion: string | null
  clientVersion: string
  principalUser: string | null
  status: string
  envelopeSchemaVersion: number
  enrolledUtc: string
  // Optional, not just nullable: the API omits null properties entirely, so every one of these
  // arrives as `undefined` when absent. Declaring them `string | null` alone is what let a strict
  // `=== null` check classify every never-checked-in device as healthy.
  lastSeenUtc?: string | null
  lastCheckInUtc?: string | null
  lastFeedETag?: string | null
  checkInCount: number
  activePolicies: { policyId: string; revision: number; activatedUtc?: string | null }[]
  lastRejectionReason?: string | null
  lastRejectionPolicyId?: string | null
  addresses: {
    adapterName: string
    address: string
    prefixLength: number | null
    macAddress: string | null
    dnsSuffix: string | null
  }[]
  groups: { deviceGroupId: string; name: string; kind: string }[]
  liveCredentialCount: number
  /**
   * When this device was last re-enrolled while its existing credential still worked.
   *
   * Absent for almost every device. A reimaged machine and somebody arriving with a stolen install id
   * are indistinguishable at enrollment, so this is reported rather than blocked: refusing would strand
   * a legitimately rebuilt machine for up to a credential period, silently.
   */
  displacedCredentialUtc?: string | null
  /**
   * What the agent said it can run at its last check-in (client ADR-091). Absent when it did not say,
   * which is every agent before 1.2.0 and is not the same as an empty list.
   */
  capabilities?: string[] | null
}

/**
 * `withheld` is absent when it could not be determined — a device that has never checked in has never
 * said what it can read, and the stored schema version is a column default rather than a report. Null
 * and zero are different answers and the screen renders them differently (D-270).
 */
export interface DeviceDetailResponse {
  device: DeviceDetail
  deliveryDetermined: boolean
  withheld?: number | null
  withheldPolicies: WithheldPolicy[]
}

/**
 * One device's tamper-evident log, as the endpoint answers it.
 *
 * Three states rather than two, and the third is why this is a discriminated shape rather than a
 * nullable status. A device that has never submitted a chain is not a device whose chain failed: it is
 * a device no audit policy applies to, and the fleet-wide count in the shell has always kept the two
 * apart. Rendering "not verified" for it would invent an integrity finding on a machine that is doing
 * exactly what it was asked to.
 */
export interface DeviceAuditChainResponse {
  deviceId: string
  submitted: boolean
  status?: AuditChainStatus | null
}

export interface AuditChainStatus {
  deviceId: string
  lastSequence: number
  lastHash: string
  firstSeenUtc: string
  lastSeenUtc: string
  entriesTotal: number
  segmentsTotal: number
  broken: boolean
  brokenUtc?: string | null
  brokenReason?: string | null
}

export interface EventFeedRow {
  eventRecordId: string
  deviceId: string
  deviceName: string
  occurredUtc: string
  receivedUtc: string
  eventType: string
  classUid: number
  className: string
  severityId: number
  capability: string | null
  succeeded: boolean | null
  message: string | null
  matchedRuleNames: string | null
  matchCount: number | null
  /** The plan the assistant was signed in to, when one was resolved. Absent is not the free plan. */
  accountTier?: string
}

export interface EventDetail {
  summary: EventFeedRow
  principalUser: string | null
  processName: string | null
  agentId: string | null
  /** The OCSF attribute bag, as text. Everything the server did not promote to a column. */
  attributesJson: string
}

export interface ObservedMatch {
  ruleName: string | null
  term: string | null
  severity: string | null
  context: string | null
}

export interface ObservedWindow {
  windowTitle: string | null
  processName: string | null
  status: string | null
  extractionMethod: string | null
  promptText: string | null
  matches: ObservedMatch[]
}

export interface PublishResponse {
  publicationId: string
  revision: number
  compiledBytes: number
  replacedExisting: boolean
  warnings: string[]
  /** The revision this one was copied from, when the server had to publish under a new number (ADR-S-054). */
  revisedFrom?: number | null
}

export interface EnrollmentTokenResponse {
  enrollmentTokenId: string
  token: string
  expiresUtc: string
  enrollEndpoint: string
}

export interface BoundPolicySummary {
  policyId: string
  name: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly validation: ValidationMessage[] = [],
    /** Set when a retire was refused: the policies that still bind the dictionary. */
    readonly boundPolicies: BoundPolicySummary[] = [],
    /**
     * Set when deleting an organization was refused because it still holds device bodies. The screen
     * needs the count rather than the sentence, so that what it asks somebody to accept is a number
     * the server produced.
     */
    readonly tenantDeletion: TenantDeletionCounts | null = null
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const TOKEN_KEY = 'afilter.token'
const IDENTITY_KEY = 'afilter.identity'

/**
 * Where a platform administrator's own session waits while they are looking at somebody else's
 * organization.
 *
 * A separate key rather than a flag on the active one, because `request` clears the active session
 * on *any* 401 - including the one an assumed token produces when its thirty minutes are up. Sharing
 * a key would mean an expired support session signed the administrator out of the platform entirely,
 * which is a bad way to learn that a token expired.
 */
const PLATFORM_SESSION_KEY = 'afilter.platform-session'

export const session = {
  token: (): string | null => {
    try {
      return window.localStorage.getItem(TOKEN_KEY)
    } catch {
      // A private window, or a browser configured to block site data. Being signed out is a correct
      // outcome; throwing on page load is not.
      return null
    }
  },
  identity: (): SignInResponse | null => {
    try {
      const raw = window.localStorage.getItem(IDENTITY_KEY)
      return raw ? (JSON.parse(raw) as SignInResponse) : null
    } catch {
      return null
    }
  },
  save: (identity: SignInResponse): void => {
    try {
      window.localStorage.setItem(TOKEN_KEY, identity.token)
      window.localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity))
    } catch {
      // Storage is unavailable; the session lives only until this page unloads.
    }
  },
  clear: (): void => {
    try {
      window.localStorage.removeItem(TOKEN_KEY)
      window.localStorage.removeItem(IDENTITY_KEY)
    } catch {
      // Nothing to clear.
    }
  },

  /** Signing out abandons the support session and the platform one together. */
  clearAll: (): void => {
    session.clear()
    try {
      window.localStorage.removeItem(PLATFORM_SESSION_KEY)
    } catch {
      // Nothing to clear.
    }
  },

  /** The platform session parked behind an assumed one, if there is one. */
  parked: (): SignInResponse | null => {
    try {
      const raw = window.localStorage.getItem(PLATFORM_SESSION_KEY)
      return raw ? (JSON.parse(raw) as SignInResponse) : null
    } catch {
      return null
    }
  },

  /** Parks the current session and makes the assumed one active. */
  assume: (assumed: SignInResponse): void => {
    try {
      const current = window.localStorage.getItem(IDENTITY_KEY)
      // Only ever park a genuine platform session. Assuming from inside an assumed session would
      // otherwise bury the original one level deeper with no way back to it.
      if (current && !window.localStorage.getItem(PLATFORM_SESSION_KEY)) {
        window.localStorage.setItem(PLATFORM_SESSION_KEY, current)
      }
    } catch {
      // Storage is unavailable; the parked session lives only until this page unloads.
    }
    session.save(assumed)
  },

  /** Ends a support session and restores the platform one. Returns it, or null if there was none. */
  release: (): SignInResponse | null => {
    const parked = session.parked()
    try {
      window.localStorage.removeItem(PLATFORM_SESSION_KEY)
    } catch {
      // Nothing to clear.
    }
    if (parked) session.save(parked)
    else session.clear()
    return parked
  },

  /**
   * Makes a freshly assumed session active without parking anything.
   *
   * `assume` is wrong for extending one: it parks the current session when nothing is parked, and
   * the current session during an extension is the assumed one - so it would bury the platform
   * session a level deeper with no way back to it.
   */
  reassume: (next: SignInResponse): void => session.save(next),

  /**
   * Tells anything rendering the session that it changed.
   *
   * A typed subscriber list rather than a `CustomEvent` or a context. An event would put an untyped
   * string contract between two modules in the same bundle and drag `window` into the unit tests; a
   * context cannot be read from here, because this is a plain module and not a component.
   */
  subscribe: (listener: (event: SessionEvent) => void): (() => void) => {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },

  /**
   * The session is over, as far as the server is concerned.
   *
   * A support session ending restores the platform one instead of clearing everything, which is what
   * D-299 parked it for: an administrator should not be signed out of the platform entirely in order
   * to be told a thirty-minute token ran out. Until now nothing restored it, so the parked session
   * sat in storage while the console rendered dead - the comment described a behaviour the screen
   * never delivered.
   */
  /**
   * @param rejected The token the refused request actually carried, when the caller knows it.
   *
   * A screen makes several requests at once, so one expired token produces a handful of 401s and
   * only the first of them is news. The rest are answers about a session that has already been dealt
   * with, and acting on them does real damage: after a support session ends, the platform session is
   * restored under it, and the next stale 401 would clear *that* - signing an administrator out of
   * the platform to tell them a thirty-minute token had run out, which is precisely what D-299 parked
   * it to prevent. Comparing against the live token is what tells a stale answer from a new one.
   */
  expire: (reason: 'session-expired' | 'no-session', at: string, rejected?: string | null): void => {
    if (rejected && session.token() !== rejected) return

    // And nothing to end when nothing is held. Belt and braces for a caller that knows no token.
    if (!session.identity() && !session.parked()) return

    if (session.identity()?.assumedByName) {
      const restored = session.release()
      if (restored) {
        notify({ kind: 'released', identity: restored })
        return
      }
    }

    session.clear()
    notify({ kind: 'ended', reason, at })
  }
}

/**
 * The identity a support session is stored as.
 *
 * Here rather than in the screen that assumes, because extending one needs the identical shape and a
 * second hand-written copy is how the two would come to disagree about, say, whether `assumedByName`
 * is set - which is the single field the whole banner, and the refusal to renew, are driven from.
 */
export function assumedIdentity(assumed: AssumedSession, assumedByName: string): SignInResponse {
  return {
    token: assumed.token,
    expiresUtc: assumed.expiresUtc,
    tenantId: assumed.tenantId,
    accountId: '',
    displayName: assumedByName,
    roles: assumed.roles,
    tenantSlug: assumed.tenantSlug,
    tenantName: assumed.tenantName,
    mustChangePassword: false,
    assumedByName
  }
}

const listeners = new Set<(event: SessionEvent) => void>()

function notify(event: SessionEvent): void {
  for (const listener of [...listeners]) listener(event)
}

/**
 * Where the console is, so signing back in can return somebody to it.
 *
 * As the router sees it, without the organization segment, because that is what the sign-in handler
 * compares against the routes these roles can reach - and the segment is the router's basename, not a
 * route. Left on, every returning path would fail that check and land on the Board.
 */
function currentPath(): string {
  try {
    return routePath(window.location.pathname) + window.location.search
  } catch {
    return ''
  }
}

/**
 * The renewal in flight, if there is one.
 *
 * One promise shared by every caller, because ten screens mounting at once would otherwise each
 * notice the same expiring token and fire their own refresh. Released in `finally`, so the next
 * window renews again.
 */
let renewal: Promise<void> | null = null

/**
 * Renews the session if it is close to its idle window, before the request that noticed.
 *
 * Triggered by real traffic rather than by a timer, which is the whole point: a timer renews a
 * session nobody is using and turns a four-hour idle window into no window at all. Making requests
 * is what "actively working" means here.
 *
 * Awaited rather than fired and forgotten. If the caller went ahead on the old token, the very next
 * request would carry it across the boundary and produce exactly the 401 this exists to avoid.
 */
async function renewIfNeeded(path: string): Promise<void> {
  // Sign-in, /me, the password change and the refresh itself. Renewing before the refresh would
  // recurse; renewing before sign-in would be renewing a session that does not exist yet.
  if (path.startsWith('/api/v1/auth/')) return

  const identity = session.identity()
  if (!identity) return

  // A support session is never renewed - the server refuses it, and ADR-S-043 says why: the clock is
  // the only thing that reliably ends one. Asking would be a 403 on every request near the end.
  if (identity.assumedByName) return
  if (!shouldRenew(identity.expiresUtc, Date.now())) return

  renewal ??= (async () => {
    try {
      const renewed = await request<RefreshResponse>('/api/v1/auth/refresh', { method: 'POST' })
      // Merged rather than replaced, so `tenantName` and `assumedByName` survive a renewal.
      session.save({ ...identity, ...renewed })
      const current = session.identity()
      if (current) notify({ kind: 'renewed', identity: current })
    } catch {
      // Deliberately swallowed. The request somebody actually made is about to run and will produce
      // the 401 that ends the session, carrying the server's own reason with it - so reporting the
      // failure here would say the same thing twice, once without context.
    } finally {
      renewal = null
    }
  })()

  await renewal
}

/**
 * The two things a call that cannot go through `request` would otherwise lose.
 *
 * Three of them exist, each for a real reason - a zip, a CSV and an image, none of which is JSON and
 * one of which needs the content type left alone. What they must not also opt out of is renewing a
 * session that is about to lapse, and telling the console when one has ended. Both used to be
 * properties of `request` alone, so a download at the wrong moment answered 401 and left the console
 * rendering as though nothing had happened.
 */
export async function authorizedHeaders(
  path: string,
  initial?: HeadersInit
): Promise<{ headers: Headers; token: string | null }> {
  await renewIfNeeded(path)

  const headers = new Headers(initial)
  const token = session.token()
  // Set only when there is one. `Bearer null` is a string the server has to reject rather than a
  // missing header it can answer plainly.
  if (token) headers.set('Authorization', `Bearer ${token}`)

  // The token goes back to the caller so a 401 can be checked against the live one, for the reason
  // `session.expire` documents: a stale refusal must not end a session that has since been replaced.
  return { headers, token }
}

/** Ends the session if a raw response says it is over, exactly as `request` would have. */
export function endSessionIfUnauthorized(
  status: number,
  payload: { reason?: unknown },
  rejected?: string | null
): void {
  if (status !== 401) return
  session.expire(
    payload.reason === 'session-expired' ? 'session-expired' : 'no-session',
    currentPath(),
    rejected
  )
}

/**
 * @param authorization A token to send instead of the active session's.
 *
 * Only extending a support session needs this, and it needs it for a reason that is not a
 * convenience: an assumed token carries the customer's roles and never `platform-admin`
 * (`AssumeTenant`), so the assume endpoint refuses it. The token that may mint another support
 * session is the platform one parked behind it.
 */
async function request<TResponse>(
  path: string,
  init?: RequestInit,
  authorization?: string
): Promise<TResponse> {
  await renewIfNeeded(path)

  const token = authorization ?? session.token()
  const headers = new Headers(init?.headers)
  headers.set('Accept', 'application/json')
  if (init?.body) headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(path, { ...init, headers })

  if (response.status === 204) return undefined as TResponse

  const text = await response.text()
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {}

  if (response.status === 401) {
    // The token expired or was revoked. Ending it here rather than in a component means every caller
    // gets the behavior without remembering to ask for it. The server's own message is kept when it
    // sent one: sign-in also answers 401, and replacing "the email address or password is incorrect"
    // with "your session has expired" would be actively misleading.
    //
    // Sign-in is exempt from ending anything at all. A wrong password is not a session ending, and
    // telling `App` that one had would replace the sign-in screen somebody is typing into.
    const reason = payload.reason === 'session-expired' ? 'session-expired' : 'no-session'
    if (!path.startsWith('/api/v1/auth/sign-in')) session.expire(reason, currentPath(), token)

    throw new ApiError(
      401,
      typeof payload.error === 'string' ? payload.error : 'Your session has expired. Sign in again.'
    )
  }

  if (!response.ok) {
    const validation = Array.isArray(payload.validation) ? (payload.validation as ValidationMessage[]) : []
    const errors = Array.isArray(payload.errors) ? (payload.errors as string[]).join(' ') : null
    const message =
      (typeof payload.error === 'string' ? payload.error : null) ??
      errors ??
      `The request failed with status ${response.status}.`
    const boundPolicies = Array.isArray(payload.boundPolicies)
      ? (payload.boundPolicies as BoundPolicySummary[])
      : []
    const tenantDeletion =
      payload.removed && typeof payload.removed === 'object'
        ? (payload.removed as TenantDeletionCounts)
        : null
    throw new ApiError(response.status, message, validation, boundPolicies, tenantDeletion)
  }

  return payload as TResponse
}

export const api = {
  signIn: (tenantSlug: string, email: string, password: string) =>
    request<SignInResponse>('/api/v1/auth/sign-in', {
      method: 'POST',
      body: JSON.stringify({ tenantSlug, email, password })
    }),

  listPolicies: (search?: string, status?: string, limit?: number, offset?: number) => {
    const query = new URLSearchParams()
    if (search) query.set('search', search)
    if (offset) query.set('offset', String(offset))
    // Omitted means active. "all" is the only way to see retired ones, which is deliberate: a list
    // that quietly included them would make a retirement look like it had not worked.
    if (status) query.set('status', status)
    if (limit) query.set('limit', String(limit))
    const suffix = query.toString()
    return request<Page<PolicySummary>>(`/api/v1/policies${suffix ? `?${suffix}` : ''}`)
  },

  getEvent: (eventRecordId: string) => request<EventDetail>(`/api/v1/events/${eventRecordId}`),

  // Read from the byte-exact body the device sent, not from the OCSF record — the prompt is
  // deliberately not in the record, so it is not in a query result and not in a SIEM export. It is
  // fetched because somebody opened this event.
  /**
   * The words an observation carried, and whether they are still there.
   *
   * `bodyRedactedUtc` is what lets a screen tell three absences apart: an event that never carried a
   * prompt, one whose words have aged out of the stored body past the organization's prompt window,
   * and a read that failed. Without it the first two are the same empty answer, which is D-263 on the
   * screen that most depends on not inventing an absence (ADR-S-044).
   *
   * Reading this requires `prompt:read` rather than `telemetry:read`, and writes a row naming the
   * reader. A 403 here is a role answer, not a missing event.
   */
  getEventPrompt: (eventRecordId: string) =>
    request<{ windows: ObservedWindow[]; bodyRedactedUtc: string | null; redactionVersion: number | null }>(
      `/api/v1/events/${eventRecordId}/prompt`
    ),

  getPolicy: (policyId: string, revision?: number) =>
    request<PolicyRevisionDetail>(
      `/api/v1/policies/${policyId}${revision === undefined ? '' : `?revision=${revision}`}`
    ),

  validatePolicy: (document: string) =>
    request<{ messages: ValidationMessage[] }>('/api/v1/policies/validate', {
      method: 'POST',
      body: JSON.stringify({ document })
    }),

  savePolicy: (document: string, policyId?: string, notes?: string) =>
    request<{ policyId: string; revision: number; documentHash: string; validation: ValidationMessage[] }>(
      '/api/v1/policies',
      { method: 'POST', body: JSON.stringify({ policyId, document, notes }) }
    ),

  approvePolicy: (policyId: string, revision: number) =>
    request<{ documentHash: string }>(`/api/v1/policies/${policyId}/revisions/${revision}/approve`, {
      method: 'POST'
    }),

  assignPolicy: (policyId: string, targets: { kind: string; targetId?: string }[]) =>
    request<{ targetCount: number }>(`/api/v1/policies/${policyId}/assignments`, {
      method: 'PUT',
      body: JSON.stringify({ targets })
    }),

  publishPolicy: (policyId: string, revision?: number) =>
    request<PublishResponse>(`/api/v1/policies/${policyId}/publish`, {
      method: 'POST',
      body: JSON.stringify({ revision })
    }),

  // The endpoint has accepted an offset and a limit and returned `total` and `hasMore` since Phase 1,
  // and this function sent neither and read only `.items`. So the fleet screen showed the first fifty
  // of nine hundred and eighty-seven with nothing on the page saying which fifty, or that there were
  // more - the same defect `listEvents` below already carries a comment about, left standing on the
  // screen the product is named after (D-430).
  listDevices: (options: { search?: string; status?: string; offset?: number; limit?: number } = {}) => {
    const query = new URLSearchParams()
    if (options.search) query.set('search', options.search)
    if (options.status) query.set('status', options.status)
    query.set('limit', String(options.limit ?? 50))
    if (options.offset) query.set('offset', String(options.offset))
    return request<Page<DeviceSummary>>(`/api/v1/devices?${query.toString()}`)
  },

  /**
   * Takes a device out of the fleet and gives its seat back.
   *
   * `POST /devices/{id}/retire` has been routed and authorized since Phase 2 with no caller, while two
   * places in this console told an operator to use it: the seat card explains that retiring a device
   * returns its seat, and the displaced-credential banner on the device page says outright to "retire
   * this device". Both were instructions for a control that did not exist (D-433).
   *
   * Only the `retire` mode is offered. The endpoint also accepts `revoke`, which differs in what it
   * does to the data the device is still holding; that is a second act with a second consequence and
   * it needs its own words before it gets its own button.
   */
  retireDevice: (deviceId: string, reason: string) =>
    request<{ status: string }>(`/api/v1/devices/${deviceId}/retire`, {
      method: 'POST',
      body: JSON.stringify({ mode: 'retire', reason })
    }),

  /** This device's tamper-evident log, which the shell has counted fleet-wide and never shown per device. */
  deviceAuditChain: (deviceId: string) =>
    request<DeviceAuditChainResponse>(`/api/v1/devices/${deviceId}/audit-chain`),

  /** A person taking responsibility for why a chain broke. It is what makes one verifiable again. */
  acceptAuditChain: (deviceId: string) =>
    request<{ accepted: boolean }>(`/api/v1/devices/${deviceId}/audit-chain/accept`, { method: 'POST' }),

  // The endpoint has existed, tenant-scoped and working, since Phase 6. Nothing called it: there was
  // no route to a device and the rows were not links, so every fact it carries — check-in history,
  // reported schema, policies held, credentials, adapters, the last refusal — was unreachable.
  getDevice: (deviceId: string) => request<DeviceDetailResponse>(`/api/v1/devices/${deviceId}`),

  // No lifetime unless one is asked for. Omitting it is what makes the token last the tenant's
  // credential period; sending a hard-coded thirty days, as this did, silently overrode the setting
  // the operator had just chosen on the same screen.
  issueEnrollmentToken: (label: string, maximumUses = 10, lifetimeDays?: number) =>
    request<EnrollmentTokenResponse>('/api/v1/devices/enrollment-tokens', {
      method: 'POST',
      body: JSON.stringify({ label, maximumUses, lifetimeDays })
    }),

  // The response has always carried `total` and `hasMore`; the screen read only `.items` and so
  // showed the newest 50 of 6,426 without saying which 50, or that there were more.
  listEvents: (
    options: { deviceId?: string; minimumSeverity?: number; limit?: number; offset?: number } = {}
  ) => {
    const query = new URLSearchParams()
    if (options.deviceId) query.set('deviceId', options.deviceId)
    if (options.minimumSeverity !== undefined) query.set('minimumSeverity', String(options.minimumSeverity))
    query.set('limit', String(options.limit ?? 50))
    if (options.offset) query.set('offset', String(options.offset))
    return request<Page<EventFeedRow>>(`/api/v1/events?${query.toString()}`)
  }
}

// ---- Phase 2: dictionaries, templates, and fleet depth ----

export interface DictionarySummary {
  dictionaryId: string
  name: string
  description: string | null
  source: string
  status: string
  createdUtc: string
  latestRevision: number
  /** Absent, not null, when the dictionary has never been published: the server omits null members. */
  publishedRevision?: number | null
  publishedUtc?: string | null
  contentRuleCount: number
  signalRuleCount: number
}

export interface ContentRule {
  name: string
  matchAny: string[]
  severity: 'low' | 'medium' | 'high' | 'critical'
  caseSensitive?: boolean
  wholeWord?: boolean
}

export interface SignalRule {
  name: string
  matchAny: string[]
  caseSensitive?: boolean
}

export interface DictionaryDetail {
  dictionaryId: string
  name: string
  description: string | null
  source: string
  status: string
  revision: number
  revisionStatus: string
  notes: string | null
  contentRules: ContentRule[]
  signalRules: SignalRule[]
  ruleCount: number
  termCount: number
  availableRevisions: number[]
  validation: ValidationMessage[]
}

export interface PreviewMatch {
  ruleName: string
  term: string
  index: number
  context: string
  severity: string
}

export interface DictionaryPreview {
  matches: PreviewMatch[]
  signals: { ruleName: string; term: string }[]
  highestSeverity: string | null
  matchCount: number
}

export interface PolicyTemplateSummary {
  key: string
  displayName: string
  description: string
  capability: string
  requiresAgent: boolean
  triggerSummary: string
  /**
   * The assistants this template can honestly be built for. Every one, unless the template acts on a
   * subscription plan — those are offered only where the account row has actually been read, because
   * building one for an assistant that exposes no account produces a policy the server refuses.
   */
  agentKeys: string[]
}

export interface AgentCatalogSummary {
  key: string
  displayName: string
  vendor: string
  surfaceDescription: string
  surface: string
  agentId: string
  /**
   * The whole ready-made parameter block — process names, browser hosts, UI Automation selectors, OCR
   * region. The editor replaces the window-finding block wholesale when the operator picks a different
   * assistant, so it needs the block rather than a summary of it.
   */
  parameters: Record<string, unknown>
}

export interface DeviceGroupSummary {
  deviceGroupId: string
  name: string
  description: string | null
  kind: string
  cidr: string | null
  createdUtc: string
  memberCount: number
}

export const dictionaries = {
  list: (options: { search?: string; ids?: string[]; limit?: number } = {}) => {
    const query = new URLSearchParams()
    if (options.search) query.set('search', options.search)
    // An explicit id set is how the policy editor reads the dictionaries a policy binds: a tenant can
    // hold hundreds, and the bound ones must not depend on which page of a search they land on.
    if (options.ids) query.set('ids', options.ids.join(','))
    if (options.limit !== undefined) query.set('limit', String(options.limit))
    const suffix = query.toString()
    return request<Page<DictionarySummary>>(`/api/v1/dictionaries${suffix ? `?${suffix}` : ''}`)
  },

  get: (dictionaryId: string) => request<DictionaryDetail>(`/api/v1/dictionaries/${dictionaryId}`),

  create: (name: string, description?: string) =>
    request<{ dictionaryId: string; revision: number }>('/api/v1/dictionaries', {
      method: 'POST',
      body: JSON.stringify({ name, description })
    }),

  addContentRule: (
    dictionaryId: string,
    rule: { name: string; terms: string[]; severity: string; caseSensitive?: boolean; wholeWord?: boolean }
  ) =>
    request<{ revision: number; replaced: boolean; validation: ValidationMessage[] }>(
      `/api/v1/dictionaries/${dictionaryId}/content-rules`,
      { method: 'POST', body: JSON.stringify(rule) }
    ),

  removeRule: (dictionaryId: string, ruleName: string) =>
    request<{ revision: number }>(`/api/v1/dictionaries/${dictionaryId}/rules/${encodeURIComponent(ruleName)}`, {
      method: 'DELETE'
    }),

  publish: (dictionaryId: string, revision: number) =>
    request<{ revision: number; ruleCount: number }>(
      `/api/v1/dictionaries/${dictionaryId}/revisions/${revision}/publish`,
      { method: 'POST' }
    ),

  /**
   * Retires a dictionary. Refused with the binding policies named unless `force`, because publishing a
   * policy that binds a retired dictionary is refused outright and that is a worse place to find out.
   */
  retire: (dictionaryId: string, reason: string, force = false) =>
    request<{ boundPolicyCount: number }>(`/api/v1/dictionaries/${dictionaryId}/retire`, {
      method: 'POST',
      body: JSON.stringify({ reason, force })
    }),

  preview: (dictionaryId: string, sampleText: string) =>
    request<DictionaryPreview>(`/api/v1/dictionaries/${dictionaryId}/preview`, {
      method: 'POST',
      body: JSON.stringify({ sampleText })
    })
}

/** A notification a policy shows when it is violated, built in or the organization's own (ADR-S-053). */
export interface ViolationMessage {
  violationMessageId: string
  name: string
  /** The handbook section it cites, filled in for {reference} on the server at publication. */
  reference: string | null
  title: string
  message: string
  /** As the agent receives them: {reference} filled in, the agent's own placeholders left alone. */
  expandedTitle: string
  expandedMessage: string
  builtIn: boolean
  status: string
  version: number
  updatedUtc: string | null
}

export interface ViolationMessageLimits {
  title: number
  message: number
  reference: number
  name: number
}

export interface ViolationMessageDraft {
  name: string
  reference: string
  title: string
  message: string
}

export interface PolicyReference {
  policyId: string
  name: string
}

export const violationMessages = {
  list: () =>
    request<{ items: ViolationMessage[]; limits: ViolationMessageLimits }>('/api/v1/violation-messages'),

  get: (violationMessageId: string) =>
    request<{ message: ViolationMessage; usedBy: PolicyReference[] }>(`/api/v1/violation-messages/${violationMessageId}`),

  create: (draft: ViolationMessageDraft) =>
    request<{ violationMessageId: string; version: number }>('/api/v1/violation-messages', {
      method: 'POST',
      body: JSON.stringify(draft)
    }),

  /** Saving names the policies still carrying the old words, which reach the fleet only when republished. */
  update: (violationMessageId: string, draft: ViolationMessageDraft, version: number) =>
    request<{ violationMessageId: string; version: number; republish: PolicyReference[] }>(
      `/api/v1/violation-messages/${violationMessageId}`,
      { method: 'PUT', body: JSON.stringify({ ...draft, version }) }
    ),

  retire: (violationMessageId: string, force = false) =>
    request<{ boundPolicyCount: number }>(`/api/v1/violation-messages/${violationMessageId}/retire`, {
      method: 'POST',
      body: JSON.stringify({ force })
    })
}

/**
 * Everything the guided policy editor draws itself from, served from the seed catalog the validator
 * reads. A form that hard-coded the severities or the twenty-rule ceiling would keep offering an
 * operator a choice the agent had stopped accepting, and nothing in the build would notice.
 */
export interface AuthoringCatalog {
  capabilities: { name: string; displayName: string }[]
  eventNames: string[]
  conditionPresets: { label: string; expression: string }[]
  severities: string[]
  approvedConditionFunctions: string[]
  limits: {
    maximumContentRules: number
    maximumSignalRules: number
    maximumTermsPerRule: number
    maximumTermLength: number
    maximumRuleNameLength: number
    maximumNameLength: number
    maximumDescriptionLength: number
    maximumRetryAttempts: number
    releasePollSeconds: number
    maximumIntervalSeconds: number
    maximumRetrySeconds: number
  }
}

/** One place an assistant runs, and the catalog entry that finds it there. */
export interface BuilderHost {
  host: 'browser' | 'desktop' | 'terminal'
  hostLabel: string
  agentKey: string
  displayName: string
  surfaceDescription: string
}

export interface BuilderAssistant {
  key: string
  displayName: string
  vendor: string
  hosts: BuilderHost[]
}

export interface BuilderRecipe {
  key: string
  target: 'assistant' | 'computer'
  label: string
  explanation: string
  triggerSummary: string
  needsDictionary: boolean
  publishes: boolean
  sendsPromptText: boolean
  /** Whether it tells the person at the keyboard, so a violation message can be chosen for it. */
  notifies: boolean
}

/** Whether one recipe can be built for one assistant host, decided by the server and never here. */
export interface BuilderSupport {
  recipeKey: string
  agentKey: string
  supported: boolean
  reason?: string | null
  note?: string | null
}

/** How many active devices said they can watch an assistant, counted by the server. */
export interface PromptObservationReach {
  active: number
  capable: number
  incapable: number
  unknown: number
}

export interface BuilderCatalog {
  assistants: BuilderAssistant[]
  recipes: BuilderRecipe[]
  support: BuilderSupport[]
  screenshotOnDetection: { key: string; label: string; explanation: string }
  promptObservationNote: string
  promptObservationReach?: PromptObservationReach | null
}

export interface PolicyGroupMember {
  policyId: string
  name: string
  status: string
  role: 'recipe' | 'screenshot'
  agentKey?: string | null
  hostLabel?: string | null
  agentDisplayName?: string | null
  latestRevision: number
  latestStatus: string
  approvedRevision?: number | null
  /** The newest revision was written by hand rather than by the builder. */
  customized: boolean
  /** Whether it tells the person, the only kind a violation message can be chosen for. */
  notifies?: boolean
  /** The violation message it shows, or absent for its own words (ADR-S-053). */
  violationMessageId?: string | null
}

export interface PolicyGroupDetail {
  groupId: string
  name: string
  target: 'assistant' | 'computer'
  assistantKey?: string | null
  assistantDisplayName?: string | null
  recipeKey: string
  recipeLabel?: string | null
  recipeExplanation?: string | null
  screenshotOnDetection: boolean
  status: string
  createdUtc: string
  members: PolicyGroupMember[]
}

export interface PolicyGroupCreated {
  groupId: string
  name: string
  members: { policyId: string; revision: number; name: string; role: string; agentKey?: string | null }[]
  warnings: string[]
}

export interface GroupMemberPublication {
  policyId: string
  name: string
  published: boolean
  revision?: number | null
  publicationId?: string | null
  replacedExisting: boolean
  problem?: string | null
  warnings: string[]
  revisedFrom?: number | null
}

export const catalogs = {
  builder: () => request<BuilderCatalog>('/api/v1/policies/builder-catalog'),
  templates: () => request<PolicyTemplateSummary[]>('/api/v1/policies/templates'),
  agents: () => request<AgentCatalogSummary[]>('/api/v1/policies/agents'),
  authoring: () => request<AuthoringCatalog>('/api/v1/policies/authoring-catalog'),
  deviceGroups: () => request<DeviceGroupSummary[]>('/api/v1/device-groups')
}

export const policies = {
  fromTemplate: (templateKey: string, agentKey?: string, name?: string) =>
    request<{ policyId: string; revision: number; document: string; validation: ValidationMessage[] }>(
      '/api/v1/policies/from-template',
      { method: 'POST', body: JSON.stringify({ templateKey, agentKey, name }) }
    ),

  bindings: (policyId: string) => request<string[]>(`/api/v1/policies/${policyId}/dictionaries`),

  bind: (policyId: string, dictionaryIds: string[]) =>
    request<{ bindingCount: number }>(`/api/v1/policies/${policyId}/dictionaries`, {
      method: 'PUT',
      body: JSON.stringify({ dictionaryIds })
    }),

  violationMessage: (policyId: string) =>
    request<{ violationMessageId: string | null }>(`/api/v1/policies/${policyId}/violation-message`),

  /** Null goes back to the policy's own notification text. */
  useViolationMessage: (policyId: string, violationMessageId: string | null) =>
    request<{ violationMessageId: string | null }>(`/api/v1/policies/${policyId}/violation-message`, {
      method: 'PUT',
      body: JSON.stringify({ violationMessageId })
    }),

  retire: (policyId: string, reason: string) =>
    request<void>(`/api/v1/policies/${policyId}/retire`, { method: 'POST', body: JSON.stringify({ reason }) }),

  /** One builder choice, made into one policy per assistant host in a single transaction. */
  fromBuilder: (choice: {
    recipeKey: string
    assistantKey?: string | null
    agentKeys: string[]
    screenshotOnDetection: boolean
    name?: string
    dictionaryIds: string[]
    violationMessageId?: string | null
  }) =>
    request<PolicyGroupCreated>('/api/v1/policies/from-builder', { method: 'POST', body: JSON.stringify(choice) }),

  group: (groupId: string) => request<PolicyGroupDetail>(`/api/v1/policies/groups/${groupId}`),

  approveGroup: (groupId: string) =>
    request<{ approved: number; alreadyApproved: number }>(`/api/v1/policies/groups/${groupId}/approve`, { method: 'POST' }),

  /** Compiles every member before publishing any, then reports each member's outcome. */
  /** One message for every member that tells the person; the rest are named as skipped. */
  useGroupViolationMessage: (groupId: string, violationMessageId: string | null) =>
    request<{ violationMessageId: string | null; chosen: PolicyReference[]; skipped: PolicyReference[] }>(
      `/api/v1/policies/groups/${groupId}/violation-message`,
      { method: 'PUT', body: JSON.stringify({ violationMessageId }) }
    ),

  publishGroup: (groupId: string) =>
    request<{ published: number; failed: number; members: GroupMemberPublication[] }>(
      `/api/v1/policies/groups/${groupId}/publish`,
      { method: 'POST' }
    ),

  retireGroup: (groupId: string, reason: string) =>
    request<{ retired: string[] }>(`/api/v1/policies/groups/${groupId}/retire`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    })
}

// ---- alerts ----

export type AlertState = 'new' | 'acknowledged' | 'assigned' | 'resolved' | 'suppressed'

export interface AlertSummary {
  alertId: string
  deviceId: string
  state: AlertState
  severity: string
  title: string
  agentId: string | null
  matchedRuleNames: string | null
  occurrenceCount: number
  firstSeenUtc: string
  lastSeenUtc: string
  assignedTo: string | null
}

export interface AlertOccurrence {
  eventRecordId: string
  occurredUtc: string
  severity: string
  message: string | null
}

export interface AlertTransition {
  fromState: string
  toState: string
  changedUtc: string
  changedBy: string | null
  note: string | null
}

export interface AlertDetail {
  summary: AlertSummary
  processName: string | null
  resolution: string | null
  note: string | null
  acknowledgedUtc: string | null
  resolvedUtc: string | null
  occurrences: AlertOccurrence[]
  history: AlertTransition[]
}

export const alerts = {
  list: (filters: { state?: string; severity?: string; deviceId?: string } = {}) => {
    const query = new URLSearchParams()
    if (filters.state) query.set('state', filters.state)
    if (filters.severity) query.set('severity', filters.severity)
    if (filters.deviceId) query.set('deviceId', filters.deviceId)
    const suffix = query.toString()
    return request<Page<AlertSummary>>(`/api/v1/alerts${suffix ? `?${suffix}` : ''}`)
  },

  get: (alertId: string) => request<AlertDetail>(`/api/v1/alerts/${alertId}`),

  /**
   * The words this case is about.
   *
   * A separate call from `getEventPrompt` because a case and its text are not the same event: the
   * case comes from a Detection Finding and the text from an Agent Prompt Observation, two deliveries
   * from one capability. The server matches them on the tuple the fold already uses - device,
   * assistant, rule, fifteen-minute bucket - so `matched: false` means there was no observation in
   * that bucket, which is the common case for a policy that enforces without publishing.
   *
   * Requires `prompt:read` and writes a row naming the reader, exactly as the event-scoped read does.
   */
  excerpt: (alertId: string) =>
    request<{
      windows: ObservedWindow[]
      bodyRedactedUtc: string | null
      matched: boolean
      eventRecordId?: string
    }>(`/api/v1/alerts/${alertId}/excerpt`),

  move: (alertId: string, state: AlertState, body: { assignTo?: string; resolution?: string; note?: string } = {}) =>
    request<{ alertId: string; state: string }>(`/api/v1/alerts/${alertId}/${state}`, {
      method: 'POST',
      body: JSON.stringify(body)
    }),

  suppress: (body: { deviceId?: string; agentId?: string; ruleName?: string; reason: string; days?: number }) =>
    request<{ suppressionId: string; expiresUtc: string | null }>('/api/v1/alerts/suppressions', {
      method: 'POST',
      body: JSON.stringify(body)
    }),

  /**
   * Close the loop: ask for a policy that would catch this again.
   *
   * It saves a draft and nothing nearer to a device. Publishing needs an approved revision, approval
   * is a separate route under an authority this one does not grant, and the server has four
   * independent guards saying so - this client has none, because a browser is not where that is
   * decided.
   *
   * The two `saved: false` shapes are different answers and the screen tells them apart: a draft the
   * validator refused comes back with its findings so it can be fixed in the editor, and an assistant
   * that is switched off comes back as a 503 through the same handler every other assistant call uses.
   */
  draftPolicy: (alertId: string) =>
    request<CaseDraftResult>(`/api/v1/alerts/${alertId}/draft-policy`, { method: 'POST' }),

  drafts: (alertId: string) => request<CasePolicyDraft[]>(`/api/v1/alerts/${alertId}/drafts`)
}

export interface DraftScope {
  kind: string
  deviceCount: number | null
  explanation: string
}

export interface CaseDraftResult {
  saved: boolean
  policyId?: string
  revision?: number
  status?: string
  scope?: DraftScope
  requestId: string
  /** False when the policy was drafted but the case could not record that it produced one. */
  provenanceRecorded?: boolean
  provenanceProblem?: string | null
  accepted?: boolean
  findings?: ValidationMessage[]
  documentJson?: string | null
  problem?: string | null
}

export interface CasePolicyDraft {
  policyId: string
  revision: number
  assistantRequestId: string | null
  proposedScope: string | null
  proposedDeviceCount: number | null
  draftedUtc: string
  draftedBy: string | null
}

// ---- trust ----

export interface SigningAnchor {
  keyId: string
  algorithm: string
  keySizeBits: number
  publicKeyBase64: string
  activatedUtc: string
}

/**
 * How far a publication actually reached.
 *
 * Four counts because they fail at different steps, and an operator debugging a policy that is doing
 * nothing needs to know which one stopped: targeted is an intent, holding an envelope is a delivery
 * fact, reporting it is a device fact, and reporting *this* revision separates a rollout in progress
 * from one that never started.
 *
 * `devicesTargeted` and `devicesReportingActive` were hardcoded zeros on the server until D-273, so a
 * policy assigned to the whole fleet reported none. This client existed with no call site the whole
 * time, which is why nobody saw it.
 */
export interface PublicationStatus {
  policyId: string
  revision: number
  documentHash: string
  compiledBytes: number
  publishedUtc: string
  revokedUtc?: string | null
  revokeReason?: string | null
  devicesTargeted: number
  devicesHoldingEnvelope: number
  devicesReportingActive: number
  devicesReportingThisRevision: number
  /** Who the policy is assigned to. Optional because a server older than this console omits it. */
  targets?: { kind: string; targetId?: string | null }[]
}

export const trust = {
  anchor: () => request<{ anchor: SigningAnchor | null }>('/api/v1/trust/anchor'),

  rotate: (reason?: string) =>
    request<{ keyId: string; retiredKeyId: string | null; keySizeBits: number; warning: string | null }>(
      '/api/v1/trust/signing-keys',
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),

  publication: (policyId: string) => request<PublicationStatus>(`/api/v1/trust/publications/${policyId}`),

  revoke: (policyId: string, reason: string, revision?: number) =>
    request<{ revisionsRevoked: number }>(`/api/v1/trust/publications/${policyId}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ reason, revision })
    })
}

// ---- evidence ----

export type ArtifactState = 'reported' | 'requested' | 'stored' | 'expired' | 'failed'

export interface ArtifactSummary {
  artifactId: string
  deviceId: string
  contentHash: string
  mediaType: string
  state: ArtifactState
  declaredBytes: number | null
  storedBytes: number | null
  width: number | null
  height: number | null
  capturedUtc: string | null
  reportedUtc: string
  storedUtc: string | null
  expiresUtc: string | null
  decision: string | null
}

export interface PresignedRead {
  url: string
  mediaType: string
  bytes: number
  expiresUtc: string
}

export const artifacts = {
  list: (filters: { deviceId?: string; eventRecordId?: string; eventIds?: string[]; state?: string } = {}) => {
    const query = new URLSearchParams()
    if (filters.deviceId) query.set('deviceId', filters.deviceId)
    if (filters.eventRecordId) query.set('eventRecordId', filters.eventRecordId)
    // An alert folds several occurrences, so its captures are the ones belonging to all of them.
    if (filters.eventIds?.length) query.set('eventIds', filters.eventIds.join(','))
    if (filters.state) query.set('state', filters.state)
    const suffix = query.toString()
    return request<Page<ArtifactSummary>>(`/api/v1/artifacts${suffix ? `?${suffix}` : ''}`)
  },

  request: (artifactId: string) =>
    request<{ artifactId: string; state: string }>(`/api/v1/artifacts/${artifactId}/request`, { method: 'POST' }),

  // The image itself is read straight from object storage with this short-lived URL. Proxying it
  // through the API would put megabytes of screenshot through the application for every view.
  content: (artifactId: string) => request<PresignedRead>(`/api/v1/artifacts/${artifactId}/content`),

  review: (artifactId: string, decision: string, note?: string) =>
    request<{ recorded: boolean }>(`/api/v1/artifacts/${artifactId}/review`, {
      method: 'POST',
      body: JSON.stringify({ decision, note })
    })
}

/**
 * The tenant's credential period, with the two derived numbers the server works out rather than the
 * console. Keeping the "fourteen days early" figure server-side means a console that has not been
 * redeployed cannot disagree with the fleet about when a device is handed its next key.
 */
export interface CredentialPeriod {
  periodMonths: number
  configured: boolean
  offeredMonths: number[]
  leadDays: number
  rotateAfterDays: number
}

/**
 * How many devices this organization may enroll, and how many it has.
 *
 * `inUse` counts active devices only, so retiring a machine gives its seat back. The suggested
 * numbers are one-click values, not the permitted set: the field accepts anything up to `ceiling`,
 * because a deployment does not know how large its largest customer is.
 */
export interface SeatLimit {
  seatLimit: number
  configured: boolean
  inUse: number
  remaining: number
  suggested: number[]
  ceiling: number
}

export interface RetentionSettings {
  artifactDays: number
  eventDays: number
  deadLetterDays: number
  /** False when no tenant has chosen and these are the deployment's defaults (D-276). */
  configured?: boolean
}

export const credentialPeriod = {
  get: () => request<CredentialPeriod>('/api/v1/devices/credential-period'),

  set: (periodMonths: number) =>
    request<CredentialPeriod>('/api/v1/devices/credential-period', {
      method: 'PUT',
      body: JSON.stringify({ periodMonths })
    })
}

export interface EnrollmentTokenSummary {
  enrollmentTokenId: string
  label: string
  tokenPrefix: string
  maximumUses: number
  uses: number
  createdUtc: string
  expiresUtc: string
  revokedUtc?: string | null
  spent: boolean
}

export const enrollmentTokens = {
  list: () => request<EnrollmentTokenSummary[]>('/api/v1/devices/enrollment-tokens'),

  revoke: (enrollmentTokenId: string) =>
    request<{ revoked: boolean }>(`/api/v1/devices/enrollment-tokens/${enrollmentTokenId}/revoke`, {
      method: 'POST'
    })
}

/**
 * One way into this organization: an enrollment token, and the kit built around it when there was one.
 *
 * `deploymentPackageId` absent means a bare token - the same credential with no installer wrapped
 * around it - or a kit built before the column recording its token was written. Both are still ways
 * in, which is why the server left-joins from the token rather than from the kit.
 *
 * `state` is derived server-side so the list can be filtered and counted on it. The browser must not
 * recompute it from the other fields: the precedence matters (a revoked token that is also exhausted
 * reads `revoked`) and a second implementation is a second answer.
 */
export interface WayIn {
  enrollmentTokenId: string
  deploymentPackageId?: string | null
  name: string
  tokenPrefix: string
  maximumUses: number
  uses: number
  state: 'live' | 'used-up' | 'expired' | 'revoked'
  clientVersion?: string | null
  policyCount?: number | null
  createdUtc: string
  expiresUtc: string
  revokedUtc?: string | null
}

/**
 * A page of ways in, and the counts the filter row is drawn from.
 *
 * `counts` is fleet-wide and arrives with the page. Reducing over `items` to produce it is the defect
 * this endpoint exists to end - the old screen said "95 tokens can enroll a machine right now" from an
 * array the server had truncated at a hundred (D-437).
 */
export interface WayInPage {
  items: WayIn[]
  total: number
  offset: number
  limit: number
  counts: Record<string, number>
}

export interface WaysInQuery {
  state?: string
  search?: string
  sort?: string
  offset?: number
  limit?: number
}

export const waysIn = {
  list: (options: WaysInQuery = {}) => {
    const query = new URLSearchParams()
    if (options.state) query.set('state', options.state)
    if (options.search) query.set('search', options.search)
    if (options.sort) query.set('sort', options.sort)
    if (options.offset) query.set('offset', String(options.offset))
    query.set('limit', String(options.limit ?? 25))
    return request<WayInPage>(`/api/v1/devices/ways-in?${query.toString()}`)
  }
}

export const seatLimit = {
  get: () => request<SeatLimit>('/api/v1/devices/seat-limit'),

  set: (value: number) =>
    request<SeatLimit>('/api/v1/devices/seat-limit', {
      method: 'PUT',
      body: JSON.stringify({ seatLimit: value })
    })
}

export const retention = {
  get: () => request<RetentionSettings>('/api/v1/retention'),

  set: (settings: Partial<RetentionSettings>) =>
    request<{ applied: boolean }>('/api/v1/retention', { method: 'PUT', body: JSON.stringify(settings) })
}

export interface DeadLetterSummary {
  deadLetterId: string
  deviceId: string | null
  eventType: string | null
  stage: string
  failure: string
  bodyBytes: number
  recordedUtc: string
  replayedUtc: string | null
  replayCount: number
}

export interface DeadLetterBody {
  deadLetterId: string
  deviceId: string | null
  eventType: string | null
  failure: string
  body: string
}

export const deadLetters = {
  list: (openOnly = true) => request<Page<DeadLetterSummary>>(`/api/v1/dead-letters?openOnly=${openOnly}`),

  get: (deadLetterId: string) => request<DeadLetterBody>(`/api/v1/dead-letters/${deadLetterId}`),

  replay: (deadLetterId: string) =>
    request<{ eventRecordId: string }>(`/api/v1/dead-letters/${deadLetterId}/replay`, { method: 'POST' })
}

// ---- query ----

export interface QueryField {
  name: string
  column: string
  kind: string
  description: string
}

export interface QueryProblem {
  message: string
  position: number
  suggestion: string | null
}

export interface EventQueryRow {
  eventRecordId: string
  deviceId: string
  deviceName: string
  occurredUtc: string
  eventType: string
  className: string
  severityId: number
  capability: string | null
  agentId: string | null
  principalUser: string | null
  processName: string | null
  message: string | null
  matchedRuleNames: string | null
  matchCount: number | null
}

export interface EventQueryResult {
  items: EventQueryRow[]
  total: number
  limit: number
  offset: number
  elapsedMilliseconds: number
  problems: QueryProblem[]
}

export interface SavedQuerySummary {
  savedQueryId: string
  name: string
  description: string | null
  expression: string
  createdUtc: string
  lastRunUtc: string | null
  runCount: number
}

export const explore = {
  fields: () => request<QueryField[]>('/api/v1/query/fields'),

  validate: (expression: string) =>
    request<{ isValid: boolean; problems: QueryProblem[]; fields: QueryField[] }>(
      `/api/v1/query/validate?expression=${encodeURIComponent(expression)}`
    ),

  run: (expression: string, offset = 0, limit = 100) =>
    request<EventQueryResult>(
      `/api/v1/query?expression=${encodeURIComponent(expression)}&offset=${offset}&limit=${limit}`
    ),

  /**
   * The largest result the export endpoint will return, from `QueryEventsHandler.MaximumLimit`.
   *
   * Stated here so the screen can say so. The endpoint asks for this many rows and no more, so an
   * export of a 6,426-row result is the first 500 of it — which is worth saying out loud next to a
   * count the same screen prints in full.
   */
  exportLimit: 500,

  /**
   * Fetched with the token, not opened as a link.
   *
   * This replaced an ordinary `<a href>` at the API route, which could not work: the token lives in
   * memory and a navigation carries no headers, so every click answered 401 and replaced the console
   * with the browser's error page. The comment defending the old shape said the API never had to hold
   * a whole result set in memory — it does, at `InsightsEndpoints.cs`, which buffers the CSV before
   * returning it, so the reasoning was wrong as well as the mechanism.
   */
  exportCsv: async (expression: string, format: 'csv' | 'json' = 'csv') => {
    const path = `/api/v1/query/export?expression=${encodeURIComponent(expression)}&format=${format}`
    const { headers, token } = await authorizedHeaders(path, { Accept: 'text/csv' })
    const response = await fetch(path, { headers })

    if (!response.ok) {
      const problem = await response.json().catch(() => ({ error: null }))
      endSessionIfUnauthorized(response.status, problem, token)
      throw new ApiError(
        response.status,
        problem.error ?? `The export failed with status ${response.status}.`
      )
    }

    await saveAsDownload(response, `coastline-events.${format}`)
  },

  saved: () => request<SavedQuerySummary[]>('/api/v1/saved-queries'),

  save: (name: string, expression: string, description?: string) =>
    request<{ savedQueryId: string }>('/api/v1/saved-queries', {
      method: 'POST',
      body: JSON.stringify({ name, expression, description })
    }),

  runSaved: (savedQueryId: string) => request<EventQueryResult>(`/api/v1/saved-queries/${savedQueryId}/run`),

  deleteSaved: (savedQueryId: string) =>
    request<void>(`/api/v1/saved-queries/${savedQueryId}`, { method: 'DELETE' })
}

// ---- dashboards ----

export interface DeviceActivityRow {
  deviceId: string
  deviceName: string
  day: string
  eventCount: number
  findingCount: number
  failureCount: number
  highestSeverity: number
  lastEventUtc: string
}

export interface DetectionTrendRow {
  day: string
  ruleName: string
  matchCount: number
  eventCount: number
  deviceCount: number
  highestSeverity: number
}

export interface TopologyDevice {
  deviceId: string
  deviceName: string
  operatingSystem: string | null
  status: string
  activePolicies: number
  lastSeenUtc: string | null
}

export interface SubnetGroup {
  subnet: string
  deviceCount: number
  activeDevices: number
  devicesWithNoPolicy: number
  totalPolicies: number
  lastSeenUtc: string | null
  devices: TopologyDevice[]
}

export interface FleetTopology {
  subnets: SubnetGroup[]
  deviceCount: number
  projectedUtc: string | null
}

export interface ProjectionState {
  projection: string
  rebuiltUtc: string | null
  appliedCount: number
}

/** One assistant, and what reached it. `agentId` is null where the agent could not attribute it. */
export interface DestinationCount {
  agentId: string | null
  detections: number
  devices: number
  highestSeverity: number
}

export const insights = {
  activity: (days = 14) => request<DeviceActivityRow[]>(`/api/v1/insights/activity?days=${days}`),

  detections: (days = 30) => request<DetectionTrendRow[]>(`/api/v1/insights/detections?days=${days}`),

  destinations: (days = 14, limit = 10) =>
    request<DestinationCount[]>(`/api/v1/insights/destinations?days=${days}&limit=${limit}`),

  topology: () => request<FleetTopology>('/api/v1/insights/topology'),

  rebuildTopology: () => request<{ devices: number }>('/api/v1/insights/topology/rebuild', { method: 'POST' }),

  projections: () => request<ProjectionState[]>('/api/v1/insights/projections'),

  rebuild: (projection: string) =>
    request<{ projection: string; rows: number }>(`/api/v1/insights/projections/${projection}/rebuild`, {
      method: 'POST'
    })
}

// ---- coverage ----

/**
 * How much of the fleet is reporting, and how much of it is actually carrying a policy.
 *
 * Shown on every screen rather than on a dashboard, which is the redesign's one editorial insistence:
 * the coverage gap is the thing an operator most needs not to forget, and a number that only appears
 * on a page somebody opens on Mondays is forgotten by Tuesday.
 *
 * `devicesCarryingPolicy` is named for what the server measured - the device listed at least one
 * policy at its last check-in - rather than for what the console calls it. A device can carry a
 * policy that observes and blocks nothing, and an `enforcing` field would be wrong about it in a way
 * nobody could see (D-318).
 *
 * The three audit-chain counts are three answers, not two: verified, broken, and never submitted one
 * at all. The third is not a failure - it is a device no audit policy applies to - and folding it
 * into either of the others would invent an integrity finding or claim a guarantee nobody made.
 */
export interface FleetCoverage {
  devicesEnrolled: number
  devicesReporting: number
  devicesCarryingPolicy: number
  devicesCarryingNoPolicy: number
  auditChainsVerified: number
  auditChainsBroken: number
  auditChainsAbsent: number
  reportingWindowHours: number
  oldestCheckInUtc: string | null
}

/**
 * One person's exposure over the window.
 *
 * Keyed on `principalUser`, the account signed in to the machine, because it is on every event.
 * `assistantAccount` is the account signed in to the assistant - the one a director actually means -
 * and it is shown beside the key rather than instead of it: it only exists where an account read
 * resolved, and a null is not "they had no account", it is "nobody could see" (ADR-S-040).
 *
 * `exposureScore` is a published formula, not a judgment. `GET /api/v1/insights/exposure-formula`
 * returns the weights and cut-offs that produced it so the screen can show its working.
 */
export interface PersonExposure {
  principalUser: string
  assistantAccount: string | null
  deviceCount: number
  detections: number
  personalAccountEvents: number
  distinctDestinations: number
  highestSeverityId: number
  topDestination: string | null
  exposureScore: number
  exposureBand: 'low' | 'watch' | 'elevated' | 'severe'
  formulaVersion: number
}

export interface PeopleExposurePage {
  items: PersonExposure[]
  /** Everybody the fleet saw at all, so the screen can honestly say "8 of 214". */
  peopleSeen: number
  windowDays: number
  formulaVersion: number
}

export interface ExposureFormulaDescription {
  version: number
  terms: { name: string; weight: number; input: string; denominator: string }[]
  bands: { name: string; from: number; to: number }[]
}

export const people = {
  list: (days = 30, limit = 50) =>
    request<PeopleExposurePage>(`/api/v1/insights/people?days=${days}&limit=${limit}`),

  formula: () => request<ExposureFormulaDescription>('/api/v1/insights/exposure-formula')
}

export const coverage = {
  get: (windowHours = 24) => request<FleetCoverage>(`/api/v1/coverage?windowHours=${windowHours}`)
}

// ---- deployment ----

export interface ClientBuild {
  clientBuildId: string
  version: string
  fileName: string
  contentHash: string
  bytes: number
  channel: string
  notes: string | null
  registeredUtc: string
}

/** One policy as a kit carried it. */
export interface BundledPolicy {
  policyId: string
  revision: number
  name: string
  capability: string
  documentHash: string
  bytes: number
}

/** The record the server kept of what one kit held. */
export interface DeploymentManifest {
  schemaVersion: string
  tenantSlug: string
  name: string
  clientVersion: string
  clientFileName: string
  clientHash: string
  collectorUri: string
  signingAnchorKeyId?: string | null
  createdUtc: string
  policies: BundledPolicy[]
}

export interface DeploymentKitSummary {
  deploymentPackageId: string
  name: string
  clientVersion: string
  /**
   * The token written into this kit's install script.
   *
   * Absent for a kit built before the column was written, and absent rather than null because the API
   * omits null members. It is what makes a kit and its token the same object to any caller that holds
   * both (D-440).
   */
  enrollmentTokenId?: string | null
  policyCount: number
  contentHash: string
  bytes: number
  createdUtc: string
}

export interface CheckInScheduleSummary {
  checkInScheduleId: string
  /**
   * Absent, not null, for the schedule that covers every device: the API serializes with
   * `WhenWritingNull`. Test it for falsiness rather than against `null`, or the organization's own
   * schedule - the one this console edits - is invisible to the code looking for it.
   */
  deviceGroupId?: string | null
  kind: string
  timesPerDay: number | null
  localTimes: string[]
  timeZoneId: string
  retryUntilSuccess: boolean
  retrySeconds: number
  updatedUtc: string
}

/** The browser's own time zone, by IANA name, or undefined where it cannot say. */
export function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

export const deployment = {
  builds: () => request<ClientBuild[]>('/api/v1/client-builds'),

  registerBuild: (version: string, fileName: string, channel = 'stable') =>
    request<{ contentHash: string; bytes: number }>('/api/v1/client-builds', {
      method: 'POST',
      body: JSON.stringify({ version, fileName, channel })
    }),

  kits: () => request<DeploymentKitSummary[]>('/api/v1/deployment-kits'),

  /**
   * What a kit contained, as the server recorded it when the kit was built.
   *
   * Routed, authorized and tested since Phase 5 with no caller anywhere in this console. It is the
   * honest answer to "which policies did the machines installed from that kit get", which is the
   * question a second download is usually being asked for - and a second download is the one thing
   * this product deliberately cannot do (ADR-S-029).
   *
   * It carries no enrollment token by design, and says so in its own doc comment. The prefix on the
   * list row is the whole of what an operator gets, which is enough to recognize one by.
   */
  manifest: (deploymentPackageId: string) =>
    request<DeploymentManifest>(`/api/v1/deployment-kits/${deploymentPackageId}`),

  /**
   * Builds a kit and saves it.
   *
   * The response is a zip rather than JSON and the server never keeps it, so this goes around the
   * shared request helper: it needs the bytes, and it has to hand them to the browser as a download
   * in the same turn rather than storing them anywhere.
   */
  // No device limit. How many machines a kit may enroll is the organization's seat limit, set once on
  // the Enrollment tab and read by the server when it builds the kit.
  buildKit: async (name: string) => {
    const { headers, token } = await authorizedHeaders('/api/v1/deployment-kits', {
      'Content-Type': 'application/json'
    })
    const response = await fetch('/api/v1/deployment-kits', {
      method: 'POST',
      headers,
      // The README in the kit shows its times in this zone, because a person reads it; the manifest
      // beside it stays in UTC, because a tool does.
      body: JSON.stringify({ name, timeZoneId: browserTimeZone() })
    })

    if (!response.ok) {
      const problem = await response.json().catch(() => ({ error: 'The kit could not be built.' }))
      endSessionIfUnauthorized(response.status, problem, token)
      throw new ApiError(response.status, problem.error ?? 'The kit could not be built.')
    }

    await saveAsDownload(response, 'coastline-deployment-kit.zip')
  },

  schedules: () => request<CheckInScheduleSummary[]>('/api/v1/check-in-schedules'),

  setSchedule: (schedule: {
    deviceGroupId?: string
    kind: string
    timesPerDay?: number
    localTimes?: string[]
    timeZoneId?: string
    retryUntilSuccess?: boolean
    retrySeconds?: number
  }) => request<{ localTimes: string[] }>('/api/v1/check-in-schedules', {
    method: 'PUT',
    body: JSON.stringify(schedule)
  })
}

function readFileName(response: Response): string | null {
  const disposition = response.headers.get('content-disposition')
  const match = disposition?.match(/filename="?([^";]+)"?/i)
  return match?.[1] ?? null
}

/**
 * Hands a fetched response to the browser as a file.
 *
 * Shared rather than copied because the alternative to it is what Export CSV used to do: point an
 * ordinary anchor at an API route. Every request in this console authenticates with a bearer token
 * held in memory, and a link navigation carries no headers, so that export answered 401 and replaced
 * the console with the browser's own error page, losing whatever query the operator had composed.
 * A download that needs the token has to be a fetch, and a fetch has to be handed to the browser
 * like this.
 */
export async function saveAsDownload(response: Response, fallbackName: string): Promise<void> {
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = readFileName(response) ?? fallbackName
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export interface ExportDestination {
  destinationId: string
  name: string
  format: string
  transport: string
  host: string | null
  port: number | null
  url: string | null
  useTls: boolean
  facility: number
  minimumSeverity: number
  messageFormat: string
  enabled: boolean
  lastSuccessUtc: string | null
  lastFailureUtc: string | null
  lastFailure: string | null
  consecutiveFailures: number
  pendingDeliveries: number
  parkedDeliveries: number
}

export interface FormatPreview {
  format: string
  payload: string
  bytes: number
  framing: string
  accepted: boolean
}

export interface ExportPreview {
  className: string
  severityId: number
  formats: FormatPreview[]
  source: string
}

export interface DestinationDraft {
  name: string
  format: string
  transport: string
  host?: string | null
  port?: number | null
  url?: string | null
  useTls: boolean
  facility?: number
  minimumSeverity?: number
  messageFormat?: string
  enabled: boolean
}

export const exports_ = {
  destinations: () => request<{ items: ExportDestination[] }>('/api/v1/export/destinations'),

  save: (draft: DestinationDraft, destinationId?: string) =>
    request<{ destinationId: string }>(
      destinationId ? `/api/v1/export/destinations/${destinationId}` : '/api/v1/export/destinations',
      { method: destinationId ? 'PUT' : 'POST', body: JSON.stringify(draft) }
    ),

  remove: (destinationId: string) =>
    request<{ deleted: boolean }>(`/api/v1/export/destinations/${destinationId}`, { method: 'DELETE' }),

  replay: (destinationId: string) =>
    request<{ replayed: number }>(`/api/v1/export/destinations/${destinationId}/replay`, { method: 'POST' }),

  /**
   * What a SIEM would receive.
   *
   * With no arguments it renders the built-in sample, which is what makes the page useful on a
   * server that has not stored an event yet — the question "what will this look like in my console"
   * is asked before the first device is enrolled, not after.
   */
  preview: (request_?: { eventRecordId?: string; destinationId?: string }) =>
    request<ExportPreview>('/api/v1/export/preview', {
      method: 'POST',
      body: JSON.stringify(request_ ?? {})
    })
}

export interface AssistantSettings {
  available: boolean
  enabled: boolean
  model: string
  provider: string
  monthlyTokenBudget: number | null
  monthlySpendCapUsd: number | null
  maxOutputTokens: number
  tokensUsedThisMonth: number
  spentThisMonthUsd: number
  requestsThisMonth: number
  unavailable: string | null
}

export interface AssistantModelSummary {
  id: string
  name: string
  contextLength: number | null
  promptPricePerMillion: number
  completionPricePerMillion: number
}

export interface AssistantDraft {
  succeeded: boolean
  available: boolean
  accepted: boolean
  documentJson: string | null
  findings: ValidationMessage[]
  requestId: string
  redactions: number
  costUsd: number
  problem: string | null
}

export interface AssistantExplanation {
  succeeded: boolean
  available: boolean
  explanation: string | null
  costUsd: number
  problem: string | null
}

export interface DraftedRule {
  name: string
  terms: string[]
  severity: string | null
  isSignal: boolean
}

export interface AssistantDictionaryDraft {
  succeeded: boolean
  available: boolean
  accepted: boolean
  rules: DraftedRule[]
  findings: ValidationMessage[]
  redactions: number
  problem: string | null
}

export interface ReviewedTerm {
  term: string
  words: number
  specific: boolean
  reason: string
  suggestion: string | null
}

export interface TermReview {
  succeeded: boolean
  available: boolean
  terms: ReviewedTerm[]
  advice: string | null
  problem: string | null
}

export interface AssistantAuditEntry {
  requestId: string
  purpose: string
  model: string
  redactedPrompt: string
  redactions: number
  responseExcerpt: string | null
  promptTokens: number
  completionTokens: number
  costUsd: number
  latencyMilliseconds: number
  outcome: string
  problem: string | null
  createdUtc: string
}

export type CredentialSource = 'file' | 'database' | 'none'

export interface ProviderCredentialStatus {
  provider: string
  configured: boolean
  source: CredentialSource
  /** The last four characters of the stored key. Never the key. */
  hint: string | null
  updatedUtc: string | null
  /** False while a mounted secret is supplying the key, because storing one would have no effect. */
  canStore: boolean
  unavailable: string | null
}

export const assistant = {
  settings: () => request<AssistantSettings>('/api/v1/assistant/settings'),

  saveSettings: (settings: {
    enabled: boolean
    model?: string
    monthlyTokenBudget?: number | null
    monthlySpendCapUsd?: number | null
    maxOutputTokens?: number
  }) => request<{ saved: boolean }>('/api/v1/assistant/settings', {
    method: 'PUT',
    body: JSON.stringify(settings)
  }),

  models: (all = false) =>
    request<{ models: AssistantModelSummary[]; fromProvider: boolean; selected: string }>(
      `/api/v1/assistant/models${all ? '?all=true' : ''}`
    ),

  draftPolicy: (description: string) =>
    request<AssistantDraft>('/api/v1/assistant/policy-draft', {
      method: 'POST',
      body: JSON.stringify({ description })
    }),

  explainPolicy: (policyId: string, revision?: number) =>
    request<AssistantExplanation>('/api/v1/assistant/policy-explain', {
      method: 'POST',
      body: JSON.stringify({ policyId, revision })
    }),

  draftDictionary: (description: string) =>
    request<AssistantDictionaryDraft>('/api/v1/assistant/dictionary-draft', {
      method: 'POST',
      body: JSON.stringify({ description })
    }),

  reviewTerms: (terms: string[], askTheModel = true) =>
    request<TermReview>('/api/v1/assistant/term-review', {
      method: 'POST',
      body: JSON.stringify({ terms, askTheModel })
    }),

  audit: () => request<{ items: AssistantAuditEntry[] }>('/api/v1/assistant/audit'),

  // The credential is write-only. The status says where the key in use comes from and shows its last
  // four characters; there is no route that returns the key itself, deliberately.
  credential: () => request<ProviderCredentialStatus>('/api/v1/assistant/credential'),

  saveCredential: (apiKey: string) =>
    request<{ saved: boolean }>('/api/v1/assistant/credential', {
      method: 'PUT',
      body: JSON.stringify({ apiKey })
    }),

  clearCredential: () =>
    request<{ saved: boolean }>('/api/v1/assistant/credential', { method: 'DELETE' })
}

// ---- console preferences ----

/**
 * The palette and dashboard arrangement belonging to the signed-in account.
 *
 * Typed against `preferences.ts` rather than restating the shapes, because the merge rules that
 * decide what a saved layout means live there and both ends have to agree about the field names.
 */
/**
 * Who can sign in to this organization, and the record of who from the platform has looked at it.
 *
 * Owner-only on the server. The console hides the screen from everyone else as a courtesy, not as a
 * control - the policy is enforced where it cannot be edited.
 */
export const accounts = {
  list: (search?: string, limit = 200) => {
    const query = new URLSearchParams({ limit: String(limit) })
    if (search) query.set('search', search)
    return request<Page<AccountSummary>>(`/api/v1/accounts?${query.toString()}`)
  },

  create: (email: string, displayName: string, roles: string[], initialPassword: string) =>
    request<{ created: boolean; accountId: string }>('/api/v1/accounts', {
      method: 'POST',
      body: JSON.stringify({ email, displayName, roles, initialPassword })
    }),

  setRoles: (accountId: string, roles: string[]) =>
    request<{ applied: boolean }>(`/api/v1/accounts/${accountId}/roles`, {
      method: 'PUT',
      body: JSON.stringify({ roles })
    }),

  setStatus: (accountId: string, status: string) =>
    request<{ applied: boolean }>(`/api/v1/accounts/${accountId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    }),

  /**
   * Lets somebody back in after ten wrong guesses, without giving them a new password.
   *
   * Idempotent at the server, deliberately: a lockout can expire between the row being drawn and the
   * button being pressed, and that is not an error worth reporting.
   */
  unlock: (accountId: string) =>
    request<{ applied: boolean }>(`/api/v1/accounts/${accountId}/unlock`, { method: 'POST' }),

  passwordPolicy: () => request<PasswordPolicy>('/api/v1/accounts/password-policy'),

  setPasswordPolicy: (maxAgeDays: number, warnDays: number) =>
    request<{ applied: boolean }>('/api/v1/accounts/password-policy', {
      method: 'PUT',
      body: JSON.stringify({ maxAgeDays, warnDays })
    }),

  resetPassword: (accountId: string, newPassword: string) =>
    request<{ applied: boolean }>(`/api/v1/accounts/${accountId}/password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword })
    }),

  assumptions: () => request<Page<TenantAssumption>>('/api/v1/accounts/assumptions'),

  changeOwnPassword: (currentPassword: string, newPassword: string) =>
    request<{ changed: boolean }>('/api/v1/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword })
    })
}

/** The organizations on this deployment, for whoever runs it. */
export const platform = {
  listTenants: (search?: string, status?: string) => {
    const query = new URLSearchParams()
    if (search) query.set('search', search)
    if (status) query.set('status', status)
    const suffix = query.toString()
    return request<TenantPage>(`/api/v1/platform/tenants${suffix ? `?${suffix}` : ''}`)
  },

  createTenant: (
    name: string,
    slug: string,
    ownerEmail: string,
    ownerDisplayName: string,
    ownerPassword: string
  ) =>
    request<{ created: boolean; tenantId: string; signingKeyId: string | null }>(
      '/api/v1/platform/tenants',
      {
        method: 'POST',
        body: JSON.stringify({ name, slug, ownerEmail, ownerDisplayName, ownerPassword })
      }
    ),

  setTenantStatus: (tenantId: string, status: string, reason: string) =>
    request<{ applied: boolean }>(`/api/v1/platform/tenants/${tenantId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, reason })
    }),

  /**
   * Destroys an organization. Present only where the deployment enabled it, so the screen asks
   * listTenants whether to offer it rather than finding out from a 404.
   *
   * acceptDataLoss is false on the first attempt on purpose. The server refuses while the
   * organization still holds device bodies and says how many, and that refusal is what the screen
   * shows before asking again - so the number somebody is agreeing to lose is the server's count
   * rather than one this client worked out for itself.
   */
  deleteTenant: (tenantId: string, confirmSlug: string, acceptDataLoss: boolean) =>
    request<{ deleted: boolean; removed: TenantDeletionCounts | null }>(
      `/api/v1/platform/tenants/${tenantId}`,
      { method: 'DELETE', body: JSON.stringify({ confirmSlug, acceptDataLoss }) }
    ),

  /**
   * A customer organization's accounts, for a platform administrator helping somebody there back in.
   *
   * Each takes the platform token explicitly, because the screen that calls them runs inside a
   * support session: the active token there carries the customer's roles and never `platform-admin`,
   * so these routes would refuse it. The platform session parked behind it is the one with the
   * authority, the same reason `extendAssumed` takes it (D-533).
   */
  listTenantAccounts: (tenantId: string, search?: string, platformToken?: string) => {
    const query = new URLSearchParams({ limit: '200' })
    if (search) query.set('search', search)
    return request<Page<AccountSummary>>(
      `/api/v1/platform/tenants/${tenantId}/accounts?${query.toString()}`,
      undefined,
      platformToken
    )
  },

  resetTenantAccountPassword: (tenantId: string, accountId: string, newPassword: string, platformToken?: string) =>
    request<{ applied: boolean }>(
      `/api/v1/platform/tenants/${tenantId}/accounts/${accountId}/password`,
      { method: 'POST', body: JSON.stringify({ newPassword }) },
      platformToken
    ),

  unlockTenantAccount: (tenantId: string, accountId: string, platformToken?: string) =>
    request<{ applied: boolean }>(
      `/api/v1/platform/tenants/${tenantId}/accounts/${accountId}/unlock`,
      { method: 'POST' },
      platformToken
    ),

  setTenantAccountStatus: (tenantId: string, accountId: string, status: string, platformToken?: string) =>
    request<{ applied: boolean }>(
      `/api/v1/platform/tenants/${tenantId}/accounts/${accountId}/status`,
      { method: 'PUT', body: JSON.stringify({ status }) },
      platformToken
    ),

  listAdministrators: () => request<PlatformAdminSummary[]>('/api/v1/platform/administrators'),

  createAdministrator: (email: string, displayName: string, initialPassword: string) =>
    request<{ created: boolean; accountId: string }>('/api/v1/platform/administrators', {
      method: 'POST',
      body: JSON.stringify({ email, displayName, initialPassword })
    }),

  setAdministratorStatus: (accountId: string, status: string) =>
    request<{ applied: boolean }>(`/api/v1/platform/administrators/${accountId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    }),

  unlockAdministrator: (accountId: string) =>
    request<{ applied: boolean }>(`/api/v1/platform/administrators/${accountId}/unlock`, { method: 'POST' }),

  resetAdministratorPassword: (accountId: string, newPassword: string) =>
    request<{ applied: boolean }>(`/api/v1/platform/administrators/${accountId}/password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword })
    }),

  assume: (tenantId: string, role: string, reason: string) =>
    request<AssumedSession>(`/api/v1/platform/tenants/${tenantId}/assume`, {
      method: 'POST',
      body: JSON.stringify({ role, reason })
    }),

  /**
   * Extends a support session by opening a new one, on the platform token parked behind it.
   *
   * Not a renewal, and deliberately not one. ADR-S-043 caps a support session at thirty minutes
   * because an administrator who forgets to hand an organization back is the normal case and the
   * clock is the only thing that reliably closes that window; renewing it silently would remove the
   * mechanism. Assuming again writes a second row in the customer's own record (D-289), so an
   * extension is a decision somebody made and the customer can read, rather than a prolongation
   * nobody sees.
   *
   * The parked token is required rather than convenient: the active one is the assumed session's,
   * and it carries the customer's roles and never `platform-admin`, so the endpoint refuses it.
   */
  extendAssumed: (tenantId: string, role: string, reason: string, platformToken: string) =>
    request<AssumedSession>(
      `/api/v1/platform/tenants/${tenantId}/assume`,
      { method: 'POST', body: JSON.stringify({ role, reason }) },
      platformToken
    )
}

export const console_ = {
  get: () => request<ConsolePreference>('/api/v1/console/preferences'),

  // keepalive lets the browser finish this request after the page it started on has gone. Preferences
  // are saved in the background as they are changed, and without it a toggle followed straight away by
  // a navigation is canceled in flight — the operator sees the change apply, then finds it undone the
  // next time they open the page. The payload is far below the 64 kB keepalive limit.
  save: (preference: ConsolePreference) =>
    request<{ saved: boolean }>('/api/v1/console/preferences', {
      method: 'PUT',
      body: JSON.stringify(preference),
      keepalive: true
    })
}

// ---- branding ----

/**
 * What an organization looks like.
 *
 * `publicBranding` is the one call in this file that works with no token, because a sign-in page has
 * to be branded before anybody has signed in. It answers identically for an organization that does
 * not exist and one that has chosen nothing, which is deliberate: sign-in is non-enumerating by
 * design and a 404 here would hand that oracle back one level up.
 */
export const branding = {
  publicBranding: (query: string) =>
    request<PublicBranding>(`/api/v1/branding${query ? `?${query}` : ''}`),

  markUrl: (query: string) => `/api/v1/branding/mark${query ? `?${query}` : ''}`,

  settings: () => request<TenantBranding>('/api/v1/branding/settings'),

  swatches: () => request<string[]>('/api/v1/branding/swatches'),

  save: (settings: { brandAccent: string | null; signInDomain: string | null; reportFooter: string | null }) =>
    request<{ applied: boolean }>('/api/v1/branding/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    }),

  /**
   * The mark itself, sent as the bytes rather than as a form or a base64 string.
   *
   * Its own fetch rather than `request`, which sets a JSON content type for every body it is given -
   * and here the content type is the thing the server validates. The error shape is read back the
   * same way so a caller cannot tell the difference.
   */
  async uploadMark(file: File): Promise<void> {
    const { headers, token } = await authorizedHeaders('/api/v1/branding/mark', {
      'Content-Type': file.type,
      Accept: 'application/json'
    })

    const response = await fetch('/api/v1/branding/mark', { method: 'PUT', headers, body: file })
    if (response.ok) return

    const text = await response.text()
    const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {}
    endSessionIfUnauthorized(response.status, payload, token)
    throw new ApiError(
      response.status,
      typeof payload.error === 'string' ? payload.error : `The mark was refused with status ${response.status}.`
    )
  }
}
